import { useState, FormEvent } from 'react';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9u3lnf8l97n3eas3go0';

export default function Contact() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢你的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    setFormStatus('submitting');
    setFormMessage('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          params.append(key, String(value));
        }
      });

      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      const code = (parsed as { code?: string })?.code;
      const meta = (parsed as { meta?: { message?: string; detail?: string } })?.meta;
      const serverMsg = meta?.message || meta?.detail || (parsed as { message?: string })?.message || responseText;

      if (response.ok && code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢你的留言，我们会尽快回复！');
        form.reset();
      } else if (serverMsg && String(serverMsg).toLowerCase().includes('spam')) {
        setFormStatus('error');
        setFormMessage(String(serverMsg));
      } else {
        setFormStatus('error');
        setFormMessage(serverMsg ? String(serverMsg) : '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络后重试');
    }
  };

  return (
    <section className="py-14 md:py-20" id="contact">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
            联系我们
          </h2>
          <p className="text-foreground-600">有任何问题或建议？欢迎给我们留言</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-10">
          {/* Contact Info */}
          <div className="flex-shrink-0 w-full lg:w-72">
            <div className="bg-background-100 rounded-xl p-6 space-y-5">
              {[
                { icon: 'ri-mail-line', label: '电子邮箱', value: 'hello@haijiao.shequn.cn' },
                { icon: 'ri-map-pin-line', label: '所在地', value: '中国浙江杭州' },
                { icon: 'ri-time-line', label: '工作时间', value: '周一至周五 9:00 - 18:00' },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`${item.icon} text-sm text-accent-500 w-4 h-4 flex items-center justify-center`}></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-0.5">{item.label}</p>
                    <p className="text-sm text-foreground-800 font-medium">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Form */}
          <div className="flex-1">
            <form
              onSubmit={handleSubmit}
              data-readdy-form=""
              className="bg-background-100 rounded-xl p-6 space-y-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1">
                    姓名 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    placeholder="请输入你的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1">
                    邮箱 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    placeholder="请输入你的邮箱"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1">
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                  placeholder="请输入留言主题"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1">
                  留言内容 <span className="text-accent-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all resize-none"
                  placeholder="请输入你想说的话（500字以内）"
                ></textarea>
              </div>

              {/* Honeypot */}
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="ab-9872-field"
              />

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full py-3 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {formStatus === 'submitting' ? '提交中...' : '发送留言'}
              </button>

              {formStatus === 'success' && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 text-center">
                  {formMessage}
                </div>
              )}
              {formStatus === 'error' && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 text-center">
                  {formMessage}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}